import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PostsService } from '@app/core';
import { SHARED_MODULES } from '@app/shared';
import { safeDate, safeEmptyList } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { Store } from '@ngrx/store';
import {
  addMonths,
  endOfMonth,
  endOfYear,
  format,
  getDay,
  getDaysInMonth,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  isWeekend,
  setDate,
  startOfDay,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';
import { first, head, isEmpty, isEqual, last, size, sortBy } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { FilterService } from '../../services/filter.service';

@Component({
  selector: 'app-calendar-post',
  imports: [SHARED_MODULES],
  templateUrl: './calendar-post.component.html',
  styleUrls: ['./calendar-post.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarPostComponent implements OnInit {
  protected week = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
  protected blankDays$: Observable<any[]>;
  protected days$: Observable<any[]>;
  protected period$: Observable<Date[]>;
  protected daysWithPosts$: Observable<string[]>;
  protected enableDateSelection$: Observable<boolean>;
  protected readonly dateSelected = signal<Date>(undefined);
  protected readonly calendar$ = new BehaviorSubject<Date>(undefined);
  protected readonly loading = signal(true);
  private readonly _destroy = inject(DestroyRef);
  private readonly _store = inject(Store);
  private readonly _filterService = inject(FilterService);
  private readonly _postsService = inject(PostsService);

  constructor() {
    this.period$ = this.loadPeriod();
  }

  public ngOnInit() {
    this.loadDate();
  }

  public onChangeDay(date: Date) {
    this.changeDay(date);
  }

  public prevMonth(params: any) {
    const threshold = head<Date>(params.period);
    const target = subMonths(this.calendar$.value, 1);
    if (isBefore(threshold, target)) {
      this.calendar$.next(target);
    }
  }

  public nextMonth(params: any) {
    const threshold = last<Date>(params.period);
    const target = addMonths(this.calendar$.value, 1);
    if (isAfter(threshold, target)) {
      this.calendar$.next(target);
    }
  }

  public minMonth(params: any) {
    const threshold = head<Date>(params.period);
    const target = subMonths(this.calendar$.value, 1);
    return isAfter(threshold, target);
  }

  public maxMonth(params: any) {
    const threshold = last<Date>(params.period);
    const target = addMonths(this.calendar$.value, 1);
    return isBefore(threshold, target);
  }

  public isToday(day: Date) {
    return isToday(day);
  }

  public isWeekend(day: Date) {
    return isWeekend(day);
  }

  public daySelected(day: Date) {
    return isSameDay(day, this.dateSelected());
  }

  public hasPosts(day: Date, daysWithPosts: string[]): boolean {
    return daysWithPosts?.some((date) => format(day, 'yyyy-MM-dd') === date);
  }

  private loadDate() {
    this.days$ = this.calendar$.pipe(
      takeUntilDestroyed(this._destroy),
      map((date) => Array.from({ length: getDaysInMonth(date) }).map((_, i) => startOfDay(setDate(date, i + 1)))),
    );

    this.blankDays$ = this.calendar$.pipe(
      takeUntilDestroyed(this._destroy),
      map((date) => getDay(startOfMonth(date))),
      map((length) => Array.from({ length })),
      map((length) => (size(length) === 7 ? [] : length)),
    );

    const monthChange$ = this.calendar$.pipe(
      takeUntilDestroyed(this._destroy),
      distinctUntilChanged((previus, current) => isEqual(startOfMonth(previus), startOfMonth(current))),
    );
    const filter$ = this._filterService.filter$.pipe(
      takeUntilDestroyed(this._destroy),
      distinctUntilChanged(isEqual),
      tap((data) => {
        this.calendar$.next(data.date_min);
        this.dateSelected.set(isSameDay(data.date_min, data.date_max) ? data.date_min : null);
      }),
    );
    this.daysWithPosts$ = combineLatest([filter$, monthChange$]).pipe(
      takeUntilDestroyed(this._destroy),
      filter(([filter, date]) => !!filter && !!date),
      map(([filter, date]) => ({
        ...filter,
        date_min: format(startOfMonth(date), 'yyyy-MM-dd'),
        date_max: format(endOfMonth(date), 'yyyy-MM-dd'),
      })),
      distinctUntilChanged(isEqual),
      tap(() => this.loading.set(true)),
      switchMap((filter) => this._postsService.daysCalendar(filter).pipe(safeEmptyList())),
      map((days) => days.map((day) => format(safeDate(day), 'yyyy-MM-dd'))),
      tap(() => this.loading.set(false)),
    );
  }

  private changeDay(date: Date) {
    if (!this.daySelected(date)) {
      this.calendar$.next(date);
      this.dateSelected.set(date);
      this._filterService.sidenav((value) => ({ ...value, date_min: date, date_max: date }));
    }
  }

  private loadPeriod(): Observable<Date[]> {
    return this._store.select(AppSelectors.ActiveUser).pipe(
      takeUntilDestroyed(this._destroy),
      map(({ bimestres, ano_letivo }) => bimestres?.filter((bimestre) => bimestre.ano === ano_letivo)),
      map((bimestres) => {
        if (!isEmpty(bimestres)) {
          const start = startOfMonth(safeDate(first<any>(sortBy(bimestres, 'data_inicio')).data_inicio));
          const end = endOfMonth(safeDate(last<any>(sortBy(bimestres, 'data_fim')).data_fim));
          return [start, end];
        }
        return [startOfYear(subYears(new Date(), 1)), endOfYear(new Date())];
      }),
    );
  }
}
